import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';

function ContactForm({ onSubmit }) {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = (formData) => {
    const newErrors = {};
    
    if (!formData.get('from_name')?.trim()) {
      newErrors.from_name = 'Name is required';
    }
    
    if (!formData.get('user_email')?.trim()) {
      newErrors.user_email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.get('user_email'))) {
      newErrors.user_email = 'Please enter a valid email address';
    }
    
    if (!formData.get('message')?.trim()) {
      newErrors.message = 'Message is required';
    }
    
    return newErrors;
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(form.current);
    const validationErrors = validateForm(formData);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setErrors({});
    setIsSubmitting(true);

    try {
      await emailjs.sendForm('service_oq3qiqi', 'template_ikhdi0x', form.current, {
        publicKey: 'qsXObpCSyLZS5GqZJ',
      });
      
      console.log('SUCCESS!');
      form.current.reset();
      if (onSubmit) {
        onSubmit(Object.fromEntries(formData));
      }
    } catch (error) {
      console.log('FAILED...', error.text);
      setErrors({ submit: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='w-full max-w-2xl mx-auto'>
      <form ref={form} onSubmit={sendEmail} className='space-y-6'>
        {/* Name Field */}
        <div>
          <label htmlFor='from_name' className='block text-sm font-medium text-slate-700 mb-2'>
            Name *
          </label>
          <input 
            type="text" 
            id="from_name"
            name="from_name" 
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
              errors.from_name ? 'border-red-500' : 'border-slate-300'
            }`}
            placeholder="Your full name"
          />
          {errors.from_name && (
            <p className='mt-1 text-sm text-red-600 flex items-center gap-1'>
              <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/>
              </svg>
              {errors.from_name}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor='user_email' className='block text-sm font-medium text-slate-700 mb-2'>
            Email Address *
          </label>
          <input 
            type="email" 
            id="user_email"
            name="user_email" 
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
              errors.user_email ? 'border-red-500' : 'border-slate-300'
            }`}
            placeholder="your.email@example.com"
          />
          {errors.user_email && (
            <p className='mt-1 text-sm text-red-600 flex items-center gap-1'>
              <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/>
              </svg>
              {errors.user_email}
            </p>
          )}
        </div>

        {/* Project Type Field */}
        <div>
          <label htmlFor='project_type' className='block text-sm font-medium text-slate-700 mb-2'>
            Project Type
          </label>
          <select 
            id="project_type"
            name="project_type" 
            className='w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200'
          >
            <option value="">Select a project type</option>
            <option value="web-development">Web Development</option>
            <option value="mobile-app">Mobile App Development</option>
            <option value="game-development">Game Development</option>
            <option value="ar-vr">AR/VR Development</option>
            <option value="consultation">General Consultation</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor='message' className='block text-sm font-medium text-slate-700 mb-2'>
            Message *
          </label>
          <textarea 
            id="message"
            name="message" 
            rows={5}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-vertical ${
              errors.message ? 'border-red-500' : 'border-slate-300'
            }`}
            placeholder="Tell us about your project, timeline, budget, or any specific requirements..."
          />
          {errors.message && (
            <p className='mt-1 text-sm text-red-600 flex items-center gap-1'>
              <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/>
              </svg>
              {errors.message}
            </p>
          )}
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
            <p className='text-sm text-red-600 flex items-center gap-2'>
              <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/>
              </svg>
              {errors.submit}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <div className='flex justify-center pt-4'>
          <button 
            type='submit' 
            disabled={isSubmitting}
            className={`btn-primary inline-flex items-center gap-2 px-8 py-3 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className='w-4 h-4 animate-spin' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' />
                </svg>
                Sending...
              </>
            ) : (
              <>
                Send Message
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 19l9 2-9-18-9 18 9-2zm0 0v-8' />
                </svg>
              </>
            )}
          </button>
        </div>

        {/* Required Fields Note */}
        <p className='text-sm text-slate-500 text-center'>
          * Required fields
        </p>
      </form>
    </div>
  );
}

export default ContactForm;